import { useState } from 'react';
import { useCustom, useCustomMutation, useGo } from '@refinedev/core';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircle, Edit, GripVertical, Plus, Trash2, Workflow, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormHeader } from '@/components/forms';
import { DataTable, type BulkAction, type Column, type SortState } from '@/components/tables';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { LeadFunnel, LeadStatusConfig } from '../types';
import { LeadFunnelFields, LeadFunnelStageFields } from './form';

type DeleteTarget =
  | { kind: 'funnel'; item: LeadFunnel }
  | { kind: 'funnels'; items: LeadFunnel[] }
  | { kind: 'stage'; item: LeadStatusConfig };

type FunnelDialogState =
  | { mode: 'create' }
  | { mode: 'edit'; funnel: LeadFunnel }
  | null;

type StagesDialogState = { funnel: LeadFunnel } | null;
type StageDialogState =
  | { mode: 'create'; funnel: LeadFunnel }
  | { mode: 'edit'; funnel: LeadFunnel; stage: LeadStatusConfig }
  | null;

function sortByPosition<T extends { position: number; id: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.position - b.position || a.id - b.id);
}

function SortableStageItem({
  stage,
  onEdit,
  onDelete,
}: {
  stage: LeadStatusConfig;
  onEdit: (stage: LeadStatusConfig) => void;
  onDelete: (stage: LeadStatusConfig) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
      }}
      className="flex items-center gap-3 rounded-md border bg-background p-3"
    >
      <button
        type="button"
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: stage.color }} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{stage.name}</span>
          {stage.isInitial && <Badge variant="secondary">Старт</Badge>}
          {stage.isFinal && <Badge variant="secondary">Финал</Badge>}
        </div>
        {stage.description && (
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{stage.description}</p>
        )}
      </div>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(stage)}>
        <Edit className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-destructive hover:text-destructive"
        onClick={() => onDelete(stage)}
        disabled={stage.isSystem}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

function getDeleteTitle(deleteTarget: DeleteTarget | null): string {
  if (!deleteTarget) {
    return 'Удалить?';
  }
  if (deleteTarget.kind === 'funnels') {
    return `Удалить воронки: ${deleteTarget.items.length}?`;
  }
  return deleteTarget.kind === 'funnel' ? 'Удалить воронку?' : 'Удалить этап?';
}

function getDeleteDescription(deleteTarget: DeleteTarget | null): string {
  if (!deleteTarget) {
    return '';
  }
  if (deleteTarget.kind === 'funnels') {
    return 'Выбранные воронки без этапов будут удалены. Воронки по умолчанию и воронки с этапами удалить нельзя.';
  }
  return deleteTarget.kind === 'funnel'
    ? `Воронка "${deleteTarget.item.name}" будет удалена.`
    : `Этап "${deleteTarget.item.name}" будет удалён.`;
}

export function LeadFunnelsList() {
  useScrollRestoration();
  const go = useGo();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [funnelDialog, setFunnelDialog] = useState<FunnelDialogState>(null);
  const [stagesDialog, setStagesDialog] = useState<StagesDialogState>(null);
  const [stageDialog, setStageDialog] = useState<StageDialogState>(null);
  const [page, setPage] = usePersistedState('leads-funnels:page', 1);
  const [search, setSearch] = usePersistedState('leads-funnels:search', '');
  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'active' | 'inactive'>('leads-funnels:statusFilter', 'all');
  const [sort, setSort] = usePersistedState<SortState | undefined>('leads-funnels:sort', { field: 'position', order: 'asc' });
  const pageSize = 20;

  const funnelsQuery = useCustom<LeadFunnel[]>({ url: '/leads/funnels', method: 'get' });
  const stagesQuery = useCustom<LeadStatusConfig[]>({ url: '/leads/statuses', method: 'get' });
  const deleteMutation = useCustomMutation();
  const updateMutation = useCustomMutation();
  const stageReorderMutation = useCustomMutation();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const funnels = sortByPosition(funnelsQuery.query?.data?.data ?? []);
  const stages = sortByPosition(stagesQuery.query?.data?.data ?? []);
  const isLoading = (funnelsQuery.query?.isLoading ?? false) || (stagesQuery.query?.isLoading ?? false);
  const nextFunnelPosition = funnels.length === 0 ? 0 : Math.max(...funnels.map((funnel) => funnel.position)) + 1;

  const refetchFunnels = () => {
    funnelsQuery.query?.refetch?.();
    stagesQuery.query?.refetch?.();
  };

  const handleDelete = () => {
    if (!deleteTarget) {
      return;
    }

    if (deleteTarget.kind === 'funnels') {
      const removableFunnels = deleteTarget.items.filter((funnel) => {
        const funnelStages = stages.filter((stage) => stage.leadFunnelId === funnel.id);
        return !funnel.isDefault && funnelStages.length === 0;
      });
      Promise.all(removableFunnels.map((funnel) => deleteMutation.mutateAsync({
        url: `/leads/funnels/${funnel.id}`,
        method: 'delete',
        values: {},
      }))).then(() => {
        setDeleteTarget(null);
        refetchFunnels();
      }).catch(() => {});
      return;
    }

    const url = deleteTarget.kind === 'funnel' ? `/leads/funnels/${deleteTarget.item.id}` : `/leads/statuses/${deleteTarget.item.id}`;

    deleteMutation.mutate(
      { url, method: 'delete', values: {} },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          refetchFunnels();
        },
      },
    );
  };

  const handleFunnelSaved = () => {
    setFunnelDialog(null);
    refetchFunnels();
  };

  const handleStageSaved = () => {
    setStageDialog(null);
    refetchFunnels();
  };

  const updateFunnelsActiveState = (selectedFunnels: LeadFunnel[], isActive: boolean) => {
    Promise.all(selectedFunnels.map((funnel) => updateMutation.mutateAsync({
      url: `/leads/funnels/${funnel.id}`,
      method: 'patch',
      values: {
        name: funnel.name,
        description: funnel.description ?? undefined,
        position: funnel.position,
        isDefault: funnel.isDefault,
        isActive,
      },
    }))).then(refetchFunnels).catch(() => {});
  };

  const handleStageDragEnd = (event: DragEndEvent) => {
    if (!stagesDialog) {
      return;
    }
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const currentStages = sortByPosition(stages.filter((stage) => stage.leadFunnelId === stagesDialog.funnel.id));
    const oldIndex = currentStages.findIndex((stage) => stage.id === active.id);
    const newIndex = currentStages.findIndex((stage) => stage.id === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    const nextStages = arrayMove(currentStages, oldIndex, newIndex);
    Promise.all(nextStages.map((stage, index) => stageReorderMutation.mutateAsync({
      url: `/leads/statuses/${stage.id}`,
      method: 'patch',
      values: { position: index },
    }))).then(refetchFunnels).catch(() => {});
  };

  const filteredFunnels = funnels.filter((funnel) => {
    const funnelStages = stages.filter((stage) => stage.leadFunnelId === funnel.id);
    const normalizedSearch = search.trim().toLowerCase();
    const matchesSearch = normalizedSearch.length === 0
      || funnel.name.toLowerCase().includes(normalizedSearch)
      || (funnel.description ?? '').toLowerCase().includes(normalizedSearch)
      || funnelStages.some((stage) => stage.name.toLowerCase().includes(normalizedSearch));
    const matchesStatus =
      statusFilter === 'all'
      || (statusFilter === 'active' && funnel.isActive)
      || (statusFilter === 'inactive' && !funnel.isActive);
    return matchesSearch && matchesStatus;
  });

  const sortedFunnels = [...filteredFunnels].sort((a, b) => {
    if (!sort) {
      return a.position - b.position || a.id - b.id;
    }
    const direction = sort.order === 'asc' ? 1 : -1;
    if (sort.field === 'name') {
      return a.name.localeCompare(b.name, 'ru') * direction;
    }
    if (sort.field === 'stagesCount') {
      const aStagesCount = stages.filter((stage) => stage.leadFunnelId === a.id).length;
      const bStagesCount = stages.filter((stage) => stage.leadFunnelId === b.id).length;
      return (aStagesCount - bStagesCount) * direction;
    }
    return (a.position - b.position || a.id - b.id) * direction;
  });
  const paginatedFunnels = sortedFunnels.slice((page - 1) * pageSize, page * pageSize);
  const filtersCount = statusFilter === 'all' ? 0 : 1;

  const columns: Column<LeadFunnel>[] = [
    {
      key: 'name',
      header: 'Воронка',
      sortable: true,
      render: (_, funnel) => (
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{funnel.name}</span>
            {funnel.isDefault && <Badge variant="secondary" className="text-[10px]">По умолчанию</Badge>}
          </div>
          {funnel.description && (
            <p className="line-clamp-2 text-xs text-muted-foreground">{funnel.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'isActive',
      header: 'Статус',
      width: '130px',
      render: (_, funnel) => (
        funnel.isActive ? <Badge variant="outline">Активна</Badge> : <Badge variant="secondary">Отключена</Badge>
      ),
    },
    {
      key: 'stagesCount',
      header: 'Этапы',
      sortable: true,
      width: '160px',
      render: (_, funnel) => {
        const funnelStages = stages.filter((stage) => stage.leadFunnelId === funnel.id);
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setStagesDialog({ funnel });
            }}
          >
            {funnelStages.length === 0 ? 'Нет этапов' : `${funnelStages.length} этапов`}
          </Button>
        );
      },
    },
  ];

  const bulkActions: BulkAction<LeadFunnel>[] = [
    {
      label: 'Открыть',
      icon: <Workflow className="h-4 w-4" />,
      onClick: (selectedFunnels) => {
        const [funnel] = selectedFunnels;
        if (funnel) {
          go({ to: `/leads/funnels/${funnel.id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Редактировать',
      icon: <Edit className="h-4 w-4" />,
      onClick: (selectedFunnels) => {
        const [funnel] = selectedFunnels;
        if (funnel) {
          setFunnelDialog({ mode: 'edit', funnel });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Добавить этап',
      icon: <Plus className="h-4 w-4" />,
      onClick: (selectedFunnels) => {
        const [funnel] = selectedFunnels;
        if (funnel) {
          setStageDialog({ mode: 'create', funnel });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Активировать',
      icon: <CheckCircle className="h-4 w-4" />,
      onClick: (selectedFunnels) => updateFunnelsActiveState(selectedFunnels, true),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Деактивировать',
      icon: <XCircle className="h-4 w-4" />,
      onClick: (selectedFunnels) => updateFunnelsActiveState(selectedFunnels, false),
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (selectedFunnels) => setDeleteTarget({ kind: 'funnels', items: selectedFunnels }),
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Воронки"
        description="Воронки CRM, этапы и лиды по этапам"
        icon={<Workflow className="h-5 w-5" />}
        sticky
        actions={
          <Button size="sm" onClick={() => setFunnelDialog({ mode: 'create' })}>
            <Plus className="h-4 w-4 mr-1" />
            Создать воронку
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={paginatedFunnels}
        total={sortedFunnels.length}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchValue={search}
        searchPlaceholder="Поиск по воронкам и этапам..."
        filters={
          <Select
            value={statusFilter}
            onValueChange={(value: 'all' | 'active' | 'inactive') => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Статус" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все статусы</SelectItem>
              <SelectItem value="active">Активные</SelectItem>
              <SelectItem value="inactive">Отключённые</SelectItem>
            </SelectContent>
          </Select>
        }
        filtersCount={filtersCount}
        filterTitle="Фильтры воронок"
        onResetFilters={() => {
          setStatusFilter('all');
          setPage(1);
        }}
        onRowClick={(funnel) => go({ to: `/leads/funnels/${funnel.id}` })}
        emptyMessage="Воронки не найдены"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getDeleteTitle(deleteTarget)}</AlertDialogTitle>
            <AlertDialogDescription>{getDeleteDescription(deleteTarget)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleteMutation.mutation.isPending}>
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={funnelDialog !== null} onOpenChange={(open) => !open && setFunnelDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {funnelDialog?.mode === 'edit' ? 'Редактировать воронку' : 'Создать воронку'}
            </DialogTitle>
            <DialogDescription>
              Воронка объединяет этапы и показывает лиды по этим этапам.
            </DialogDescription>
          </DialogHeader>
          {funnelDialog && (
            <LeadFunnelFields
              funnel={funnelDialog.mode === 'edit' ? funnelDialog.funnel : undefined}
              position={funnelDialog.mode === 'edit' ? funnelDialog.funnel.position : nextFunnelPosition}
              onCancel={() => setFunnelDialog(null)}
              onSuccess={handleFunnelSaved}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={stagesDialog !== null} onOpenChange={(open) => !open && setStagesDialog(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{stagesDialog ? `Этапы: ${stagesDialog.funnel.name}` : 'Этапы'}</DialogTitle>
            <DialogDescription>
              Перетащите этапы, чтобы изменить порядок воронки.
            </DialogDescription>
          </DialogHeader>
          {stagesDialog && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setStageDialog({ mode: 'create', funnel: stagesDialog.funnel })}>
                  <Plus className="h-4 w-4 mr-1" />
                  Этап
                </Button>
              </div>
              {sortByPosition(stages.filter((stage) => stage.leadFunnelId === stagesDialog.funnel.id)).length === 0 ? (
                <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                  Этапы ещё не настроены
                </div>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStageDragEnd}>
                  <SortableContext
                    items={sortByPosition(stages.filter((stage) => stage.leadFunnelId === stagesDialog.funnel.id)).map((stage) => stage.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {sortByPosition(stages.filter((stage) => stage.leadFunnelId === stagesDialog.funnel.id)).map((stage) => (
                        <SortableStageItem
                          key={stage.id}
                          stage={stage}
                          onEdit={(selectedStage) => setStageDialog({ mode: 'edit', funnel: stagesDialog.funnel, stage: selectedStage })}
                          onDelete={(selectedStage) => setDeleteTarget({ kind: 'stage', item: selectedStage })}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={stageDialog !== null} onOpenChange={(open) => !open && setStageDialog(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {stageDialog?.mode === 'edit' ? 'Редактировать этап' : 'Создать этап'}
            </DialogTitle>
            <DialogDescription>
              Этап становится колонкой на доске выбранной воронки.
            </DialogDescription>
          </DialogHeader>
          {stageDialog && (
            <LeadFunnelStageFields
              funnelId={stageDialog.funnel.id}
              status={stageDialog.mode === 'edit' ? stageDialog.stage : undefined}
              position={
                stageDialog.mode === 'edit'
                  ? stageDialog.stage.position
                  : stages.filter((stage) => stage.leadFunnelId === stageDialog.funnel.id).length
              }
              onCancel={() => setStageDialog(null)}
              onSuccess={handleStageSaved}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
